"use client";

import { useState } from 'react';
import FAQSchema from '@/components/shared/FAQSchema';

const faqs = [
    {
        question: "What is water hardness?",
        answer: "Water hardness refers to the concentration of dissolved minerals, primarily calcium and magnesium, in your water. Hard water contains high levels of these minerals, while soft water has low levels. Hardness is typically measured in grains per gallon (gpg) or parts per million (ppm)."
    },
    {
        question: "How is water hardness measured?",
        answer: "Water hardness is measured in grains per gallon (gpg) or parts per million (ppm). The classifications are: Soft (less than 3.5 gpg or 60 ppm), Moderate (3.5-7 gpg or 60-120 ppm), Hard (7-10.5 gpg or 120-180 ppm), and Very Hard (over 10.5 gpg or 180 ppm)."
    },
    {
        question: "What causes hard water?",
        answer: "Hard water occurs when water passes through mineral-rich rock formations, particularly limestone and chalk, which contain calcium and magnesium. As water flows through these formations, it dissolves these minerals, increasing the water's hardness level."
    },
    {
        question: "Is hard water bad for my health?",
        answer: "Hard water is generally safe to drink and may even provide beneficial minerals like calcium and magnesium. However, it can cause problems with plumbing, appliances, and personal care. The minerals in hard water can build up in pipes, reduce appliance efficiency, and make it harder to lather soap."
    },
    {
        question: "What are the signs of hard water?",
        answer: "Common signs of hard water include: white scale buildup on faucets and showerheads, spots on dishes and glassware, reduced soap lathering, dry skin and hair after showering, stiff or faded laundry, reduced water pressure, and higher energy bills due to inefficient water heaters."
    },
    {
        question: "How can I test my water hardness?",
        answer: "You can use our free water hardness checker tool on this page by entering your postal code or selecting your province and city. For a more accurate assessment, we offer professional water testing services that can identify not just hardness but also other water quality issues."
    },
    {
        question: "Do I need a water softener if I have hard water?",
        answer: "Whether you need a water softener depends on your water hardness level and the problems you're experiencing. If you have hard or very hard water (over 7 gpg) and notice scale buildup, appliance issues, or skin/hair problems, a water softener can significantly improve your water quality and protect your home's plumbing and appliances."
    },
    {
        question: "Can I reduce water hardness without a water softener?",
        answer: "While there are some temporary solutions like using vinegar to remove scale buildup, a water softener is the most effective long-term solution for hard water. Other filtration systems can address specific issues, but they don't remove the minerals that cause hardness. A water softener is the only system that actually softens water by removing calcium and magnesium."
    }
];

export default function WaterHardnessFAQ() {
    const [openIndex, setOpenIndex] = useState(null);

    return (
        <>
            <FAQSchema faqs={faqs} />
            <section className="py-16 sm:py-24 bg-white">
                <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    <div className="mx-auto max-w-2xl lg:text-center">
                        <h2 className="text-base font-semibold leading-7 text-sky-600">Common Questions</h2>
                        <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                            Water Hardness FAQ
                        </p>
                        <p className="mt-6 text-lg leading-8 text-gray-600">
                            Get answers to frequently asked questions about water hardness and what it means for your home.
                        </p>
                    </div>
                    <div className="mx-auto mt-16 max-w-4xl">
                        <dl className="space-y-8">
                            {faqs.map((faq, index) => (
                                <div
                                    key={index}
                                    className="border-b border-gray-200 pb-8 last:border-b-0 last:pb-0"
                                >
                                    <dt className="text-lg font-semibold leading-7 text-gray-900">
                                        <button
                                            type="button"
                                            className="flex w-full items-start justify-between text-left"
                                            onClick={() => setOpenIndex(openIndex === index ? null : index)}
                                            aria-expanded={openIndex === index}
                                        >
                                            <span>{faq.question}</span>
                                            <span className="ml-6 flex h-7 items-center">
                                                {openIndex === index ? (
                                                    <svg className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12h-15" />
                                                    </svg>
                                                ) : (
                                                    <svg className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                                    </svg>
                                                )}
                                            </span>
                                        </button>
                                    </dt>
                                    {openIndex === index && (
                                        <dd className="mt-4 pr-12">
                                            <p className="text-base leading-7 text-gray-600">{faq.answer}</p>
                                        </dd>
                                    )}
                                </div>
                            ))}
                        </dl>
                    </div>
                </div>
            </section>
        </>
    );
}

